import Image from 'next/image';

import Link from '~/shared/components/link';
import ReadMoreLink from '~/shared/components/slider/components/read-more-link';
import { getLocaleLink } from '~/shared/helpers/browser-helpers';
import { getDate, getUrl } from '~/shared/helpers/string-helpers';
import { useTypedTranslation } from '~/shared/hooks';
import type { ItemDataType } from '~/shared/types/general-type';
import type { I18nextOptionsType } from '~/shared/types/i18n-type';
import type { TagType } from '~/shared/types/tag-type';

import '~/shared/components/items-navigator/components/extended-tile-item/extended-tile-item.scss';

import noImage from '~/assets/no-image.webp';
import type { CourseDirectionType } from '~/shared/types/company-type';

type ExtendedTileItemPropsType = {
  data: ItemDataType;
  pageToGo: string;
  isContainItemImage: boolean;
  pageTagsToGo?: string;
  t: (key: string, options: I18nextOptionsType) => string;
};

const ExtendedTileItem = ({
  data,
  lng,
  readMoreText,
  getTagLink,
  pageTagsToGo,
  isContainItemImage = false,
  pageToGo,
}: ExtendedTileItemPropsType) => {
  const t = useTypedTranslation(['link']);
  const courseDirection = data?.company?.courseDirections?.find((item: CourseDirectionType) => {
    return item?._id === data?.courseDirection;
  });

  return (
    <article itemProp="blogPosts" itemScope itemType="http://schema.org/BlogPosting" className="extended-tile-item">
      <div className="extended-tile-item__wrapper">
        <Link
          aria-label={data?.title}
          itemProp="url"
          href={getLocaleLink(getUrl({ link: pageToGo, alias: data?.alias }), lng)}
        >
          <link itemProp="image" href={data?.thumbnail?.path ?? noImage.src} />
          <div className="extended-tile-item__image-wrapper">
            <Image
              loading="lazy"
              priority={false}
              fill
              src={data?.thumbnail?.path ?? noImage.src}
              alt={data?.title}
              title={data?.title}
              style={{
                objectFit: 'cover',
              }}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
            />
          </div>
        </Link>
        <div className="extended-tile-item__content">
          <div className="extended-tile-item__tags-block">
            {!data?.courseDirection && (
              <p className="extended-tile-item__tags">
                {(data?.tags ?? []).map((tag: TagType, i) =>
                  getTagLink ? (
                    <Link
                      itemProp="url"
                      className="extended-tile-item__tag"
                      key={i}
                      href={getTagLink ? getTagLink(tag) : '#'}
                    >
                      {tag?.name}
                    </Link>
                  ) : (
                    <Link
                      itemProp="url"
                      className="extended-tile-item__tag"
                      key={tag?.alias}
                      href={getTagLink ? getTagLink(tag) : '#'}
                    >
                      {tag?.name}
                    </Link>
                  ),
                )}
              </p>
            )}
            {data?.courseDirection && (
              <div
                className="extended-tile-item__direction"
                style={{
                  background: courseDirection?.backgroundColor,
                  color: courseDirection?.color,
                  borderColor: courseDirection?.color,
                }}
              >
                {courseDirection?.courseDirectionName}
              </div>
            )}
            <div className="extended-tile-item__bullet"></div>
          </div>
          <h4 itemProp="headline" className="extended-tile-item__title">
            <Link itemProp="url" href={getLocaleLink(getUrl({ link: pageToGo, alias: data?.alias }), lng)}>
              {data?.title}
            </Link>
          </h4>
          <div
            itemProp="description"
            className="extended-tile-item__text"
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: data?.description }}
          />
          {data?.createdAt && (
            <p className="extended-tile-item__date">
              <time itemProp="datePublished" dateTime={data?.createdAt}>
                {getDate(data?.createdAt, 'DATE')}
              </time>
            </p>
          )}
          <ReadMoreLink
            className="extended-tile-item__read-more"
            href={getLocaleLink(getUrl({ link: pageToGo, alias: data?.alias }), lng)}
            content={readMoreText ?? t('link.read-more')}
          />
        </div>
      </div>
    </article>
  );
};

export default ExtendedTileItem;
